import { AppState, RatingConfig, RatingSet, Theme } from "../models";

/**
 * Upgrades an AppState to a target version by merging each dataset's ratings
 * with the target version's attributeIds.  Existing values are preserved;
 * attributes that are new in the target version are initialised to `null`.
 * The returned state has `version` set to `targetVersion`.
 */
export function upgradeStateToVersion(
  state: AppState,
  targetVersion: string,
  config: RatingConfig,
): AppState {
  const targetConfig = config.versions[targetVersion];
  if (!targetConfig) return state;

  const upgradedDatasets = state.datasets.map((set) => {
    const upgraded: Record<string, number | null> = {};
    for (const id of targetConfig.attributeIds) {
      upgraded[id] = set.ratings[id] ?? null;
    }
    return { ...set, ratings: upgraded };
  });

  return { ...state, datasets: upgradedDatasets, version: targetVersion };
}

/**
 * Returns the highest version key from an array of version keys, based on their
 * order in `config.versions` (later keys are considered higher).
 */
export function resolveHighestVersion(
  versionKeys: string[],
  config: RatingConfig,
): string {
  const allVersions = Object.keys(config.versions);
  let best = versionKeys[0];
  for (const key of versionKeys) {
    if (allVersions.indexOf(key) > allVersions.indexOf(best)) {
      best = key;
    }
  }
  return best;
}

/**
 * Encodes app state into a versioned URL hash string.
 * Supports multiple datasets separated by "|".
 *
 * Format: `#<version>:<set1>|<set2>|...|<theme>`
 * Where each set is: `<r1>,<r2>,...,<rN>,<encodedName>,<optionalColor>`
 *
 * Uses `state.version` when set; falls back to `config.currentVersion`.
 */
export function encodeRatings(state: AppState, config: RatingConfig): string {
  const version = state.version ?? config.currentVersion;
  const versionConfig = config.versions[version] ?? config.versions[config.currentVersion];
  
  const encodedSets = state.datasets.map(set => {
    const ratings = versionConfig.attributeIds.map((id) => {
      const val = set.ratings[id];
      return val === null || val === undefined ? "U" : String(val);
    });
    const name = encodeURIComponent(set.name ?? "");
    const color = set.color ? encodeURIComponent(set.color) : "";
    return [...ratings, name, color].join(",");
  });

  const themeMap: Record<string, string> = { light: "l", dark: "d", system: "s" };
  const theme = themeMap[state.theme ?? "system"];
  
  return `#${version}:${encodedSets.join("|")}|${theme}`;
}

/**
 * Decodes a single dataset string into a RatingSet.
 */
function decodeSet(setStr: string, config: RatingConfig, version: string): RatingSet {
  const versionConfig = config.versions[version];
  const parts = setStr.split(",");
  const { attributeIds, minRating, maxRating } = versionConfig;
  const ratings: Record<string, number | null> = {};

  attributeIds.forEach((id, index) => {
    const part = parts[index];
    if (part === "U" || part === undefined || part === "") {
      ratings[id] = null;
      return;
    }
    const val = parseInt(part, 10);
    if (!isNaN(val) && val >= minRating && val <= maxRating) {
      ratings[id] = val;
    } else {
      ratings[id] = null;
    }
  });

  const namePart = parts[attributeIds.length];
  let name: string | undefined;
  if (namePart !== undefined) {
    try {
      name = decodeURIComponent(namePart)
        // eslint-disable-next-line no-control-regex
        .replace(/[\x00-\x1F\x7F]/g, "")
        .slice(0, 50);
    } catch { /* ignore */ }
  }

  const colorPart = parts[attributeIds.length + 1];
  let color: string | undefined;
  if (colorPart) {
    try {
      color = decodeURIComponent(colorPart);
    } catch { /* ignore */ }
  }

  return { ratings, name, color };
}

/**
 * Decodes a URL hash into app state. Supports legacy single-set format
 * and new multi-set format.
 *
 * The returned `AppState.version` is set to the version key from the hash,
 * so callers can use the correct attributeIds for display.
 */
export function decodeRatings(hash: string, config: RatingConfig): AppState {
  const state: AppState = { datasets: [], theme: "system" };

  if (!hash.startsWith("#")) return state;

  const colonIndex = hash.indexOf(":");
  if (colonIndex === -1) return state;

  const version = hash.slice(1, colonIndex);
  const versionConfig = config.versions[version];
  if (!versionConfig) return state;

  state.version = version;

  const content = hash.slice(colonIndex + 1);
  const themeRevMap: Record<string, Theme> = {
    l: "light", d: "dark", s: "system",
  };
  
  if (content.includes("|")) {
    const sections = content.split("|");
    const themePart = sections.pop();
    
    state.datasets = sections.map(s => decodeSet(s, config, version));
    if (themePart) {
      state.theme = themeRevMap[themePart] ?? "system";
    }
  } else {
    // Legacy single-set format (might have theme as extra comma-separated part)
    const parts = content.split(",");
    const expectedParts = versionConfig.attributeIds.length + 1; // ratings + name
    
    if (parts.length > expectedParts) {
        // Check if last part is a theme code
        const lastPart = parts[parts.length - 1];
        if (themeRevMap[lastPart]) {
            state.theme = themeRevMap[lastPart];
            state.datasets = [decodeSet(parts.slice(0, -1).join(","), config, version)];
        } else {
            state.datasets = [decodeSet(content, config, version)];
        }
    } else {
        state.datasets = [decodeSet(content, config, version)];
    }
  }

  return state;
}
